/**
 * Rétention locale de la table `orders` (Dexie) — source unique des règles.
 *
 * Module pur : ni React, ni Dexie, ni AppWrite.
 *
 * Deux fenêtres, calculées depuis la date du jour (et NON depuis last_updated_orders) :
 *   - ORDER_LOCAL_KEEP_MONTHS (3 mois)  : toutes les commandes, sur `$updatedAt` ;
 *   - QUOTA_LOCAL_KEEP_MONTHS (36 mois) : les types KOT et KTM (quotas, qui durent).
 *
 * Règle de suppression : une commande locale absente de la liste des ids distants
 * (union des deux fenêtres) est supprimée si elle est `done_m3`, ou si elle a déjà
 * été reçue/poussée (`last_updated` ou `last_pushed` non null). Une commande jamais
 * poussée (créée hors ligne) n'est jamais supprimée, sauf `done_m3`.
 *
 * Règle de rattrapage : les ids distants absents de la table locale sont récupérés
 * PAR LEURS IDS, jamais par `$updatedAt`. C'est précisément le critère `$updatedAt`
 * (fetch incrémental depuis last_updated_orders) qui les a fait manquer : un document
 * ancien et intact n'est plus jamais redemandé. Une commande présente en local, même
 * en statut `deleted` en attente de push, n'est jamais retéléchargée.
 */
import moment from 'moment'

export const ORDER_LOCAL_KEEP_MONTHS = 3
export const QUOTA_LOCAL_KEEP_MONTHS = 36

export interface LocalOrderRef {
   id: string
   status: string
   last_updated: string | null
   last_pushed: string | null
}

export function computeKeepCutoffs(now: Date | string): { general: string, quota: string } {
   return {
      general: moment.utc(now).subtract(ORDER_LOCAL_KEEP_MONTHS, 'months').toISOString(),
      quota: moment.utc(now).subtract(QUOTA_LOCAL_KEEP_MONTHS, 'months').toISOString(),
   }
}

export function planOrderReconciliation(
   localOrders: LocalOrderRef[],
   remoteIds: Set<string>,
): { toDelete: string[], toDownload: string[] } {
   const toDelete: string[] = []
   const localIds = new Set<string>()
   for (const o of localOrders) {
      localIds.add(o.id)
      if (remoteIds.has(o.id)) continue
      if (o.status === 'done_m3' || o.last_updated !== null || o.last_pushed !== null) {
         toDelete.push(o.id)
      }
   }
   const toDownload: string[] = []
   for (const id of remoteIds) {
      if (!localIds.has(id)) toDownload.push(id)
   }
   return { toDelete, toDownload }
}

/**
 * Décision sur la copie locale d'une commande après un `getDocument` en erreur (awReconcileOrder).
 *   - 'keep'   : on conserve la copie locale (404 après un push échoué : jamais créée sur AW) ;
 *   - 'purge'  : on supprime la copie locale (accès retiré / commande disparue) ;
 *   - 'ignore' : erreur infra/réseau, on ne touche à rien (l'appelant relance l'erreur).
 */
export type ReconcileErrorAction = 'keep' | 'purge' | 'ignore'

export function decideReconcileErrorAction(code: number | undefined | null, afterFailedPush: boolean): ReconcileErrorAction {
   if (code === 401 || code === 403) return 'purge'
   if (code === 404) return afterFailedPush ? 'keep' : 'purge'
   return 'ignore'
}
